import { useEffect, useRef } from "react";
import { asyncTaskResult } from "@/network/network";
import { useAsyncTasks } from "@/modern/components/async-tasks/hooks/useAsyncTasks";
import type { AsyncTask } from "@/modern/components/async-tasks/types";
import { unwrapAsyncTaskResultBody } from "@/modern/components/async-tasks/utils";
import { useIntegrationErrorReporter } from "./useIntegrationErrorReporter";
import type { IntegrationProvider } from "./types";

type UseAsyncTaskFailureReporterOptions = {
  matchTask: (task: AsyncTask) => boolean;
  provider: IntegrationProvider;
  fallback: string;
};

export function useAsyncTaskFailureReporter({
  matchTask,
  provider,
  fallback,
}: UseAsyncTaskFailureReporterOptions) {
  const { tasks } = useAsyncTasks();
  const { reportError } = useIntegrationErrorReporter();
  const reportedRef = useRef(new Set<number>());

  useEffect(() => {
    for (const task of tasks) {
      if (!matchTask(task) || task.status !== "Failed") continue;
      if (reportedRef.current.has(task.id)) continue;

      reportedRef.current.add(task.id);
      void (async () => {
        try {
          const response = await asyncTaskResult(task.id);
          reportError({
            error: unwrapAsyncTaskResultBody(response.data),
            provider,
            fallback,
          });
        } catch (error) {
          reportError({ error, provider, fallback });
        }
      })();
    }
  }, [tasks, matchTask, provider, fallback, reportError]);
}
